/**
 * Short-lived credential used in file URLs (<img src>, download links).
 *
 * WHY THIS EXISTS: those URLs are written into CDN access logs, browser
 * history, and any link the user forwards. Putting the seven-day session token
 * there hands all of that a master key. An asset token expires in minutes and
 * is refused by the API on anything except file routes, so a leaked URL is
 * survivable.
 *
 * WHY IT IS CACHED IN A MODULE VARIABLE: the URL builders are called inline
 * inside JSX (`src={receiptUrl(id)}`) and must stay synchronous. Fetching per
 * image would force every one of those call sites to become async state, which
 * is a rewrite of a dozen components. So the token is fetched once after login
 * and refreshed on a timer, and the builders read whatever is current.
 *
 * Deliberately NOT in localStorage: it should not outlive the tab. A fresh one
 * is a single request away.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let assetToken = null;
let refreshTimer = null;
let inFlight = null;
// Bumped on logout. A refresh that resolves after its generation has passed is
// discarded, so user A's pending mint can never populate user B's session.
let generation = 0;

/** Current asset token, or null before the first fetch completes. */
export function getAssetToken() {
  return assetToken;
}

/**
 * Fetch a fresh asset token and schedule its replacement.
 * Concurrent callers share one request.
 */
export function refreshAssetToken() {
  if (inFlight) return inFlight;

  const session = localStorage.getItem("token");
  if (!session) return Promise.resolve(null);

  const mine = generation;
  inFlight = axios
    .post(
      `${BACKEND_URL}/api/auth/asset-token`,
      {},
      { headers: { Authorization: `Bearer ${session}` }, timeout: 15000 }
    )
    .then((r) => {
      if (mine !== generation) return null;   // logged out (or switched user) mid-flight
      assetToken = r.data.token;
      // Renew at 80% of the lifetime so a token never expires mid-render.
      const ms = Math.max(30, (r.data.expires_in || 600) * 0.8) * 1000;
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        inFlight = null;
        refreshAssetToken();
      }, ms);
      return assetToken;
    })
    .catch(() => {
      // A failed refresh must not wedge the module: the next call retries.
      assetToken = null;
      return null;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Anchors bake the token into href at RENDER time, but the token rotates on a
 * timer. A page left open past a rotation would hold links pointing at an
 * expired token and 401 on click — a real break for anyone who leaves a tab
 * sitting, which is most people.
 *
 * Rewriting the href during the capture phase of the click fixes it centrally.
 * The alternative was a re-render subscription, which would have missed the two
 * pages that build file links without consuming auth context.
 *
 * Images are unaffected: they load at mount, long before any rotation.
 */
function refreshStaleLinkOnClick(e) {
  const a = e.target && e.target.closest && e.target.closest("a[href]");
  if (!a) return;
  const current = getAssetToken();
  if (!current) return;
  try {
    const u = new URL(a.href, window.location.origin);
    // ORIGIN CHECK FIRST. Without it any link on the page — including one from
    // user-supplied content — matching /api/ would be handed a live credential
    // on click. That would turn this convenience into a token exfiltration
    // vector, which is considerably worse than the stale link it fixes.
    if (u.origin !== new URL(BACKEND_URL, window.location.origin).origin) return;
    if (!u.pathname.startsWith("/api/")) return;
    if (!u.searchParams.has("token")) return;
    if (u.searchParams.get("token") === current) return;
    u.searchParams.set("token", current);
    a.href = u.toString();
  } catch {
    /* not a parseable URL — leave it alone */
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("click", refreshStaleLinkOnClick, true);
}

/** Drop the token on logout so it cannot outlive the session that authorized it. */
export function clearAssetToken() {
  generation += 1;      // invalidates any in-flight refresh
  inFlight = null;
  assetToken = null;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}
